import React from "react";
import styled from "styled-components";
import { FiSave, FiSend } from "react-icons/fi";
import { HomeHeader } from "../Home/HomeHeader";

export default function MaintenancePageShell({
    title,
    subtitle,
    loading,
    isProjectManager,
    saving,
    onSaveDraft,
    onPublish,
    homeLink,
    children,
}) {
    if (loading) {
        return (
            <>
                <HomeHeader text="Maintenance" link={homeLink} />
                <PageWrapper><Main>Loading...</Main></PageWrapper>
            </>
        );
    }

    if (!isProjectManager) {
        return (
            <>
                <HomeHeader text="Maintenance" link={homeLink} />
                <PageWrapper>
                    <Main>
                        <AccessCard>
                            <AccessTitle>Access restricted</AccessTitle>
                            <AccessText>
                                Your account does not have project manager permission for this page.
                            </AccessText>
                        </AccessCard>
                    </Main>
                </PageWrapper>
            </>
        );
    }

    return (
        <>
            <HomeHeader text="Maintenance" link={homeLink} />
            <PageWrapper>
                <Main>
                    <HeaderRow>
                        <div>
                            <PageTitle>{title}</PageTitle>
                            <PageSubtitle>{subtitle}</PageSubtitle>
                        </div>
                        <ButtonGroup>
                            <DraftButton type="button" onClick={onSaveDraft} disabled={saving}>
                                <FiSave size={18} />
                                <span>{saving ? "Saving..." : "Save Draft"}</span>
                            </DraftButton>
                            <PublishButton type="button" onClick={onPublish} disabled={saving}>
                                <FiSend size={18} />
                                <span>{saving ? "Saving..." : "Publish"}</span>
                            </PublishButton>
                        </ButtonGroup>
                    </HeaderRow>
                    {children}
                </Main>
            </PageWrapper>
        </>
    );
}

const PageWrapper = styled.div`
    min-height: calc(100vh - 80px);
    background: #f8fafc;
    padding: 24px;
`;

const Main = styled.main`
    width: min(1180px, 100%);
    margin: 0 auto;
    text-align: left;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 24px;

    @media (max-width: 720px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

const PageTitle = styled.h1`
    margin: 0;
    color: #111827;
    font-size: 1.75rem;
    line-height: 1.25;
    letter-spacing: 0;
`;

const PageSubtitle = styled.p`
    margin: 6px 0 0;
    color: #6b7280;
    font-size: 0.95rem;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 10px;
`;

const buttonBase = `
    min-height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 22px;
    border-radius: 9999px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.1s;

    &:active:not(:disabled) {
        transform: scale(0.95);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.7;
    }
`;

const DraftButton = styled.button`
    ${buttonBase}
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #374151;

    &:hover:not(:disabled) {
        background: #f3f4f6;
        border-color: #9ca3af;
    }

    &:focus-visible {
        outline: 2px solid #374151;
        outline-offset: 2px;
    }
`;

const PublishButton = styled.button`
    ${buttonBase}
    border: none;
    background: #9333ea;
    color: #ffffff;

    &:hover:not(:disabled) {
        background: #7c3aed;
    }

    &:focus-visible {
        outline: 2px solid #9333ea;
        outline-offset: 2px;
    }
`;

const AccessCard = styled.div`
    max-width: 560px;
    padding: 24px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
`;

const AccessTitle = styled.h1`
    margin: 0 0 8px;
    color: #111827;
    font-size: 1.4rem;
`;

const AccessText = styled.p`
    margin: 0;
    color: #4b5563;
    line-height: 1.55;
`;
